import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, TextField, Button,
  FormControl, InputLabel, Select, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Divider, CircularProgress, Alert, Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { apiFetch } from '../api';

const PurchaseEntry = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [billNo, setBillNo] = useState('');
  const [notes, setNotes] = useState('');
  
  const [items, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    material_id: '',
    material_name: '',
    quantity: '',
    rate: '',
    amount: 0
  });

  const [paidAmount, setPaidAmount] = useState(0);

  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const sRes = await apiFetch('/api/suppliers');
        setSuppliers(await sRes.json());
        
        const mRes = await apiFetch('/api/materials');
        setMaterials(await mRes.json());
      } catch (err) {
        setError('मास्टर्स लोड करताना त्रुटी आली.');
      }
    };
    fetchMasters();
  }, []);

  const handleAddItem = () => {
    if (!currentItem.material_id || !currentItem.quantity || !currentItem.rate) {
      alert('कृपया साहित्य, नग आणि दर भरा.');
      return;
    }
    setItems([...items, { ...currentItem, amount: currentItem.quantity * currentItem.rate }]);
    setCurrentItem({ material_id: '', material_name: '', quantity: '', rate: '', amount: 0 });
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateTotal = () => items.reduce((sum, item) => sum + item.amount, 0);

  const handleSave = async () => {
    if (!selectedSupplier || items.length === 0) {
      setError('कृपया व्यापारी आणि किमान एक साहित्य निवडा.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const total = calculateTotal();
    const payload = {
      date,
      bill_no: billNo,
      supplier_id: selectedSupplier.id,
      items: items.map(it => ({
        material_id: it.material_id,
        quantity: parseFloat(it.quantity),
        rate: parseFloat(it.rate),
        amount: parseFloat(it.amount)
      })),
      total_amount: total,
      net_amount: total, // Simplified: net = total for now
      paid_amount: parseFloat(paidAmount || 0),
      notes
    };

    try {
      const response = await apiFetch('/api/purchases', {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setSuccess('खरेदी व्यवहार यशस्वीपणे जतन झाला!');
        setItems([]);
        setBillNo('');
        setNotes('');
        setPaidAmount(0);
        setSelectedSupplier(null);
      } else {
        setError('जतन करताना तांत्रिक अडचण आली.');
      }
    } catch (err) {
      setError('सर्व्हरशी संपर्क होऊ शकला नाही.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#1B5E20' }}>
        नवीन खरेदी नोंद (New Purchase Entry)
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="तारीख" type="date" InputLabelProps={{ shrink: true }} value={date} onChange={(e) => setDate(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={suppliers}
              getOptionLabel={(option) => option.name}
              value={selectedSupplier}
              onChange={(_, newValue) => setSelectedSupplier(newValue)}
              renderInput={(params) => <TextField {...params} label="व्यापारी निवडा" />}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth label="बिल नंबर" value={billNo} onChange={(e) => setBillNo(e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, color: '#1B5E20' }}>साहित्य जोडा</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>साहित्य</InputLabel>
              <Select 
                value={currentItem.material_id} 
                label="साहित्य"
                onChange={(e) => {
                    const mat = materials.find(m => m.id === e.target.value);
                    setCurrentItem({...currentItem, material_id: e.target.value, material_name: mat?.name});
                }}
              >
                {materials.map(m => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField fullWidth label="नग (Qty)" type="number" value={currentItem.quantity} onChange={(e) => setCurrentItem({...currentItem, quantity: e.target.value})} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField fullWidth label="दर (Rate)" type="number" value={currentItem.rate} onChange={(e) => setCurrentItem({...currentItem, rate: e.target.value})} />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField fullWidth label="एकूण" value={currentItem.quantity * currentItem.rate || 0} disabled />
          </Grid>
          <Grid item xs={12} sm={2}>
            <Button fullWidth variant="contained" startIcon={<AddIcon />} onClick={handleAddItem} sx={{ backgroundColor: '#1B5E20', py: 1.8 }}> जोडा </Button>
          </Grid>
        </Grid>

        <TableContainer sx={{ mt: 3, maxHeight: '70vh', overflow: 'auto' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>साहित्य</TableCell>
                <TableCell align="right">नग</TableCell>
                <TableCell align="right">दर</TableCell>
                <TableCell align="right">एकूण</TableCell>
                <TableCell align="right">अॅक्शन</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.material_name}</TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell align="right">{item.rate}</TableCell>
                  <TableCell align="right">{item.amount}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="error" onClick={() => removeItem(index)}><DeleteIcon /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {items.length > 0 && (
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell colSpan={3} align="right"><strong>एकूण बिल (Total Amount):</strong></TableCell>
                      <TableCell align="right"><strong>₹ {calculateTotal()}</strong></TableCell>
                      <TableCell />
                  </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="तपशील / नोट्स" multiline rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth label="दिलेली रक्कम (Paid Amount)" type="number" value={paidAmount} onChange={(e) => setPaidAmount(e.target.value)} />
          </Grid>
          <Grid item xs={12} sm={4}>
             <Button 
                fullWidth variant="contained" size="large" 
                startIcon={<SaveIcon />}
                onClick={handleSave} 
                disabled={loading}
                sx={{ backgroundColor: '#0FA958', py: 1.5, fontWeight: 'bold' }}
             >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'खरेदी बिल जतन करा'}
             </Button>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default PurchaseEntry;
